"use client";
import { useEffect, useRef, useState, useMemo } from "react";
import React from "react";
import Image from "next/image";
import Link from "next/link";
import { useSiteData } from "@/contexts/SiteDataContext";
import Navbar from "@/components/Navbar";
import Footer from "@/sections/Footer";

/* ================= 滚动进入视口动画 Hook（仅用于非首屏模块） ================= */
function useReveal<T extends HTMLElement = HTMLElement>(options?: {
  threshold?: number;
  rootMargin?: string;
}) {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(max-width: 767px)").matches) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;

    // 如果在视口内，直接显示，避免 IntersectionObserver 延迟
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setVisible(true);
    }
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        threshold: options?.threshold ?? 0.12,
        rootMargin: options?.rootMargin ?? "0px 0px -8% 0px",
      },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

const revealDelay = (ms: number): React.CSSProperties =>
  ({ "--reveal-delay": `${ms}ms` }) as React.CSSProperties;

/* ================= 1. Contact Banner 组件（首屏，纯 CSS 动画） ================= */
const ContactBannerSection = ({
  contactJsonData,
  lang,
}: {
  contactJsonData: any;
  lang: string;
}) => {
  const homeText =
    contactJsonData?.["item_1791950099857_t5bkdvknr"]?.children?.[0]?.title ||
    "Home";
  const currentText =
    contactJsonData?.["item_1791950099857_t5bkdvknr"]?.children?.[1]?.title ||
    "Contact Us";
  const title =
    contactJsonData?.["item_1791950073322_tvt8wgtq8"]?.value || "CONTACT US";
  const tag =
    contactJsonData?.["item_1791950080241_r42roylq2"]?.value ||
    "Let's Build Your Aluminum Rod Production Line";
  const subtitle =
    contactJsonData?.["item_1791950088618_n5bern9ex"]?.value ||
    "Tell us your material, capacity and target market — our engineers will come back with a tailored CCR line proposal.";
  const breadcrumbIcon =
    "https://oss.yy-aicloud.com/uploads/20260923/6ab339b50d1d81a9dcba2349fef2bb823c39e45dd6c96.svg";

  return (
    <section className="contact-banner-section">
      <div className="contact-banner-content">
        <div
          className="contact-banner-top-bar css-banner-anim"
          style={{ animationDelay: "100ms" }}
        >
          <div className="contact-banner-breadcrumb">
            <span>{homeText}</span>
            {/* 首屏关键小图标加 priority，避免被懒加载 */}
            <Image
              src={breadcrumbIcon}
              alt="icon"
              width={10}
              height={10}
              priority
            />
            <span className="current">{currentText}</span>
          </div>
        </div>
        <h1
          className="contact-banner-title css-banner-anim"
          style={{ animationDelay: "200ms" }}
        >
          {title}
        </h1>
        <div
          className="contact-banner-tag css-banner-anim"
          style={{ animationDelay: "250ms" }}
        >
          {tag}
        </div>
        <p
          className="contact-banner-subtitle css-banner-anim"
          style={{ animationDelay: "300ms" }}
        >
          {subtitle}
        </p>
      </div>

      <style>{`
        .contact-banner-section {
          position: relative;
          width: 100%;
          max-width: 1920px;
          margin: 0 auto;
          min-height: 400px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          background-color: var(--color-neutral-10, #F5F5F7);
          overflow: hidden;
          padding: 80px 0 0;
        }
        .contact-banner-content {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: 1440px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 20px;
          border-bottom: 1px solid var(--color-neutral-60, #c0c0c0);
          padding-bottom: 80px;
        }
        .contact-banner-top-bar {
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }
        .contact-banner-breadcrumb {
          display: flex;
          gap: 6px;
          align-items: center;
          font-size: 14px;
          line-height: 20px;
          font-weight: 300;
          color: var(--color-neutral-60, #C0C0C0);
          margin-bottom: 20px;
        }
        .contact-banner-breadcrumb .current {
          color: var(--color-neutral-80, #666666);
        }
        .contact-banner-title {
          font-size: 64px;
          font-weight: 700;
          color: var(--color-neutral-100, #262626);
          margin: 0;
          line-height: 72px;
          word-break: break-word;
        }
        .contact-banner-tag {
          background: linear-gradient(to right, var(--color-primary-gradient-start), var(--color-primary-gradient-end));
          color: #fff;
          padding: 8px 20px;
          border-radius: 80px;
          font-size: 16px;
          line-height: 24px;
          font-weight: 300;
          display: inline-block;
          white-space: normal;
          word-break: break-word;
        }
        .contact-banner-subtitle {
          font-size: 16px;
          font-weight: 300;
          color: var(--color-neutral-100, #262626);
          line-height: 24px;
          margin: 0;
          word-break: break-word;
        }
        /* ================= 纯 CSS 首屏进场动画（不依赖 JS，不阻塞 LCP） ================= */
        @keyframes bannerFadeInUp {
          from {
            opacity: 0;
            transform: translate3d(0, 40px, 0);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }
        .css-banner-anim {
          opacity: 0;
          animation: bannerFadeInUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .css-banner-anim {
            opacity: 1;
            animation: none;
          }
        }
        @media (max-width: 1024px) {
          .contact-banner-content {
            max-width: 100%;
            padding-bottom: 40px;
          }
        }
        @media (max-width: 767px) {
          .contact-banner-section {
            padding: 40px 20px 0;
            min-height: 300px;
          }
          .contact-banner-top-bar {
            margin-bottom: 20px;
            flex-direction: column;
            align-items: flex-start;
          }
          .contact-banner-title {
            font-size: 32px;
            line-height: 1.2;
          }
          .contact-banner-tag {
            font-size: 12px;
            padding: 8px 16px;
          }
          .contact-banner-subtitle {
            font-size: 12px;
            line-height: 1.6;
          }
        }
      `}</style>
    </section>
  );
};

/* ================= 自定义下拉选择组件 ================= */
const CustomSelect = ({
  name,
  value,
  options,
  placeholder,
  onChange,
  onBlur,
  hasError,
}: {
  name: string;
  value: string;
  options: string[];
  placeholder: string;
  onChange: (e: any) => void;
  onBlur: (e: any) => void;
  hasError: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        if (isOpen) {
          setIsOpen(false);
          onBlur({ target: { name, value } });
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, name, value, onBlur]);

  const handleSelect = (opt: string) => {
    onChange({ target: { name, value: opt } });
    setIsOpen(false);
    onBlur({ target: { name, value: opt } });
  };

  return (
    <div className="custom-select-wrapper" ref={wrapperRef}>
      <div
        className={`custom-select-trigger ${hasError ? "has-error" : ""} ${isOpen ? "is-open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {value ? (
          <span className="custom-select-value">{value}</span>
        ) : (
          <span className="custom-select-placeholder">{placeholder}</span>
        )}
        <svg
          className={`custom-select-arrow ${isOpen ? "is-open" : ""}`}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 9L12 15L18 9"
            stroke="#666666"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {isOpen && (
        <div className="custom-select-options">
          {options.map((opt, idx) => (
            <div
              key={idx}
              className={`custom-select-option ${value === opt ? "is-selected" : ""}`}
              onClick={() => handleSelect(opt)}
            >
              {opt}
            </div>
          ))}
        </div>
      )}

      <style>{`
        .custom-select-wrapper {
          position: relative;
          width: 100%;
        }
        .custom-select-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 16px 20px;
          border-radius: 10px;
          background-color: var(--color-neutral-00, #fff);
          box-sizing: border-box;
          cursor: pointer;
          transition: box-shadow 0.3s ease;
          min-height: 52px;
        }
        .custom-select-trigger.has-error {
          box-shadow: 0 0 0 1px #A32D2D inset;
        }
        .custom-select-trigger.is-open {
          box-shadow: 0 0 0 1px var(--color-primary-gradient-end, #0a5694) inset;
        }
        .custom-select-placeholder {
          color: #999999;
          font-size: 14px;
          line-height: 20px;
          font-weight: 300;
          word-break: break-word;
        }
        .custom-select-value {
          color: var(--color-neutral-100, #262626);
          font-size: 14px;
          line-height: 20px;
          font-weight: 300;
          word-break: break-word;
        }
        .custom-select-arrow {
          transition: transform 0.3s ease;
          flex-shrink: 0;
        }
        .custom-select-arrow.is-open {
          transform: rotate(180deg);
        }
        .custom-select-options {
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          width: 363px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          padding: 8px 0;
          z-index: 100;
          max-height: 250px;
          overflow-y: auto;
          box-sizing: border-box;
        }
        .custom-select-option {
          padding: 10px 16px;
          font-size: 14px;
          color: var(--color-neutral-100, #262626);
          cursor: pointer;
          transition: background-color 0.2s ease;
          word-break: break-word;
        }
        .custom-select-option:hover {
          background-color: var(--color-neutral-10, #F5F5F7);
        }
        .custom-select-option.is-selected {
          color:var(--color-primary-base, #0a5694);
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

/* ================= 主表单区域 ================= */
const ContactMainSection = ({
  contactJsonData,
  lang,
}: {
  contactJsonData: any;
  lang: string;
}) => {
  const { ref: sectionRef, visible } = useReveal<HTMLElement>();

  const formNotice =
    contactJsonData?.["item_1791950232091_m09hu80ns"]?.value ||
    "Your request will be replied within 24 hours.";
  const submitText =
    contactJsonData?.["item_1791950262250_7d73dzkr3"]?.value ||
    "Send Us an Inquiry";
  const gdprText =
    contactJsonData?.["item_1791950294218_65ug0k8kt"]?.value ||
    "GDPR Consent — I agree to the Privacy Policy and consent to being contacted.";
  const submitIcon =
    "https://oss.yy-aicloud.com/uploads/20260911/6aa3bfe0d0939022e0ee5162c13d9a7bb3bd00fb032ce.svg";

  const interestOptions = useMemo(
    () =>
      contactJsonData?.["item_1791954951162_l6rlithv3"]?.children
        ?.map((item: any) => item?.value)
        .filter(Boolean) || [
        "Aluminum Rod CCR",
        "Aluminum Wire & Conductor",
        "Casting Lines",
        "Rolling Mills",
        "Copper CCR",
        "Other",
      ],
    [contactJsonData],
  );

  const formFields = useMemo(
    () => [
      {
        name: "company",
        placeholder:
          contactJsonData?.["item_1791954722858_38w39sd3l"]?.children?.[0]
            ?.placeholder || "Your Company",
        type: "text",
        required: true,
        halfWidth: true,
      },
      {
        name: "name",
        placeholder:
          contactJsonData?.["item_1791954722858_38w39sd3l"]?.children?.[1]
            ?.placeholder || "Your Name",
        type: "text",
        required: true,
        halfWidth: true,
      },
      {
        name: "email",
        placeholder:
          contactJsonData?.["item_1791954722858_38w39sd3l"]?.children?.[2]
            ?.placeholder || "Your Email",
        type: "email",
        required: true,
        halfWidth: true,
      },
      {
        name: "phone",
        placeholder:
          contactJsonData?.["item_1791954722858_38w39sd3l"]?.children?.[3]
            ?.placeholder || "Phone / WhatsApp",
        type: "text",
        required: false,
        halfWidth: true,
      },
      {
        name: "country",
        placeholder:
          contactJsonData?.["item_1791954722858_38w39sd3l"]?.children?.[4]
            ?.placeholder || "Country",
        type: "text",
        required: false,
        halfWidth: true,
      },
      {
        name: "interest",
        placeholder:
          contactJsonData?.["item_1791954722858_38w39sd3l"]?.children?.[5]
            ?.placeholder || "Interest",
        type: "select",
        required: false,
        halfWidth: true,
        options: interestOptions,
      },
      {
        name: "message",
        placeholder:
          contactJsonData?.["item_1791954722858_38w39sd3l"]?.children?.[6]
            ?.placeholder ||
          "Please briefly describe your project requirements.",
        type: "textarea",
        required: false,
        halfWidth: false,
      },
    ],
    [contactJsonData, interestOptions],
  );

  const messageDes =
    contactJsonData?.["item_1791955693747_i84jnzzre"]?.value || "Mes";
  const infoTitle =
    contactJsonData?.["item_1791955171217_nemwrzzmo"]?.value ||
    "Get In Touch Directly";
  const infoBranch =
    contactJsonData?.["item_1791955175706_pl5er7x4v"]?.value ||
    "European Branch";
  const socials = contactJsonData?.["item_1791955208193_e1vo63nkl"]
    ?.children || [
    {
      name: "facebook",
      icon: "https://oss.yy-aicloud.com/uploads/20260911/6aa3bfe0d71336271faadeedd7626d661856b7a004e27.svg",
      url: "#",
    },
    {
      name: "twitter",
      icon: "https://oss.yy-aicloud.com/uploads/20260911/6aa3bfe0d71336271faadeedd7626d661856b7a004e27.svg",
      url: "#",
    },
    {
      name: "linkedin",
      icon: "https://oss.yy-aicloud.com/uploads/20260911/6aa3bfe0d71336271faadeedd7626d661856b7a004e27.svg",
      url: "#",
    },
    {
      name: "youtube",
      icon: "https://oss.yy-aicloud.com/uploads/20260911/6aa3bfe0d71336271faadeedd7626d661856b7a004e27.svg",
      url: "#",
    },
    {
      name: "whatsapp",
      icon: "https://oss.yy-aicloud.com/uploads/20260911/6aa3bfe0d71336271faadeedd7626d661856b7a004e27.svg",
      url: "#",
    },
    {
      name: "wechat",
      icon: "https://oss.yy-aicloud.com/uploads/20260911/6aa3bfe0d71336271faadeedd7626d661856b7a004e27.svg",
      url: "#",
    },
  ];

  const contactCards = contactJsonData?.["item_1791955415451_s5ccf8lx4"]
    ?.children || [
    {
      icon: "https://oss.yy-aicloud.com/uploads/20260911/6aa3bfe0d71336271faadeedd7626d661856b7a004e27.svg",
      label: "Phone & WhatsApp",
      value: "+86 138 5108 8706",
      action: "Chat Now",
    },
    {
      icon: "https://oss.yy-aicloud.com/uploads/20260911/6aa3bfe0d71336271faadeedd7626d661856b7a004e27.svg",
      label: "Email",
      value: "sales@demccr.com",
      action: "Send Email",
    },
    {
      icon: "https://oss.yy-aicloud.com/uploads/20260911/6aa3bfe0d71336271faadeedd7626d661856b7a004e27.svg",
      label: "Address",
      value: "Unit 7, Room 101, Yougu Science and Technology Park...",
      action: "Copy",
    },
    {
      icon: "https://oss.yy-aicloud.com/uploads/20260911/6aa3bfe0d71336271faadeedd7626d661856b7a004e27.svg",
      label: "Business Hours",
      value: "Monday - Saturday 9:00 - 18:00 (GMT+8)",
      action: null,
    },
  ];

  const successData =
    contactJsonData?.["item_1791956530036_5bmod6svn"]?.children || [];
  const copySuccess =
    contactJsonData?.["item_1791957165475_jqw2pph1b"]?.value || "[Copied!]";
  const sumitLoading =
    contactJsonData?.["item_1791957262787_d0vo37rit"]?.value || "Loaing...";

  const [formState, setFormState] = useState<Record<string, any>>({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
    url: typeof window !== "undefined" ? window.location.href : "",
    interest: "",
    gdpr: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [source, setSource] = useState("");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const interestParam = params.get("interest");
    const sourceParam = params.get("source");
    if (sourceParam) setSource(sourceParam);
    if (interestParam) {
      const matchedOption = formFields.find(
        (f: any) =>
          f.name === "interest" &&
          f.options?.some((opt: string) =>
            opt
              .toLowerCase()
              .replace(/[^a-z0-9]/g, "-")
              .includes(interestParam.toLowerCase()),
          ),
      );
      if (matchedOption) {
        const exactOption = matchedOption.options.find((opt: string) =>
          opt
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "-")
            .includes(interestParam.toLowerCase()),
        );
        if (exactOption) {
          setFormState((prev) => ({ ...prev, interest: exactOption }));
        }
      }
    }
  }, [formFields]);

  const validateField = (field: any, value: string) => {
    if (field.required && !value.trim()) {
      return (
        contactJsonData?.["item_1791955983275_xsjwtn0dr"]?.value ||
        "This field is required."
      );
    }
    if (field.name === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value))
        return (
          contactJsonData?.["item_1791956054338_odso52fey"]?.value ||
          "Please enter a valid email address."
        );
    }
    if (value.length > 2000)
      return (
        contactJsonData?.["item_1791956087370_i5dh1rcx6"]?.value ||
        "Input is too long (max 2000 characters)."
      );
    return "";
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (globalError) setGlobalError("");
  };

  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    const field = formFields.find((f: any) => f.name === name);
    if (!field) return;
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const newErrors: Record<string, string> = {};
    let hasError = false;
    formFields.forEach((field: any) => {
      const error = validateField(field, formState[field.name] || "");
      if (error) {
        newErrors[field.name] = error;
        hasError = true;
      }
    });

    if (!formState.gdpr) {
      setGlobalError(
        contactJsonData?.["item_1791956160538_mezc3o3wg"]?.value ||
          "Please agree to the Privacy Policy and consent to being contacted.",
      );
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setGlobalError("");
    setIsSubmitting(true);

    try {
      const payload = {
        name: formState.name || "",
        email: formState.email || "",
        company: formState.company || "",
        phone: formState.phone || "",
        message: formState.message || "",
        url:
          formState.url ||
          (typeof window !== "undefined" ? window.location.href : ""),
        lang: lang || "en",
      };

      const response = await fetch(`/api/needs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      setIsSuccess(true);
    } catch (error) {
      console.error("Submit failed:", error);
      setGlobalError(
        contactJsonData?.["item_1791956199691_5hv6y42oo"]?.value ||
          "发送失败，请重试或直接邮件联系我们",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const fallbackCopyTextToClipboard = (text: string, idx: number) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    // 防止在复制时页面滚动
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand("copy");
      if (successful) {
        setCopiedIndex(idx);
        setTimeout(() => setCopiedIndex(null), 2000);
      } else {
        console.error("Fallback: 复制命令执行失败");
      }
    } catch (err) {
      console.error("Fallback: 无法复制文本", err);
    }
    document.body.removeChild(textArea);
  };

  const handleActionClick = (idx: number, value: string) => {
    if (idx === 0) {
      window.location.href = `tel:${value.replace(/\s+/g, "")}`;
    } else if (idx === 1) {
      window.location.href = `mailto:${value}`;
    } else if (idx === 2) {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(value)
          .then(() => {
            setCopiedIndex(idx);
            setTimeout(() => setCopiedIndex(null), 2000);
          })
          .catch((err) => {
            console.error("Clipboard API 失败，尝试降级方案", err);
            fallbackCopyTextToClipboard(value, idx);
          });
      } else {
        fallbackCopyTextToClipboard(value, idx);
      }
    }
  };

  if (isSuccess) {
    return (
      <section
        ref={sectionRef}
        className={`contact-main-section reveal-group is-visible`}
      >
        <div className="contact-success-wrapper">
          <div className="success-icon">✓</div>
          <h3>{successData?.title || "Thank you!"}</h3>
          <p>
            {successData?.desc1 ||
              "Your inquiry has been received — we'll reply within 24 hours."}
          </p>
          <div className="success-actions">
            <Link href={`/${lang}`} className="btn-primary">
              {successData?.desc2 || "Back to Home"}
            </Link>
            <Link href={`/${lang}/contact-us`} className="btn-secondary">
              {successData?.desc2 || "View Resources"}
            </Link>
          </div>
        </div>

        <style>{`
          .contact-success-wrapper { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 400px; text-align: center; gap: 20px; background-color: var(--color-neutral-10, #F5F5F7); border-radius: 20px; padding: 60px 20px; }
          .success-icon { width: 60px; height: 60px; border-radius: 50%; background: linear-gradient(to right, var(--color-primary-gradient-start), var(--color-primary-gradient-end)); color: #fff; font-size: 30px; display: flex; align-items: center; justify-content: center; }
          .contact-success-wrapper h3 { font-size: 28px; color: var(--color-neutral-100); margin: 0; }
          .contact-success-wrapper p { font-size: 16px; color: var(--color-neutral-80); margin: 0; }
          .success-actions { display: flex; gap: 16px; margin-top: 10px; }
          .btn-primary { padding: 12px 24px; border-radius: 30px; background: var(--color-neutral-100); color: #fff; text-decoration: none; font-weight: 300; transition: background 0.3s; }
          .btn-primary:hover { background: linear-gradient(to right, var(--color-primary-gradient-start), var(--color-primary-gradient-end)); }
          .btn-secondary { padding: 12px 24px; border-radius: 30px; border: 1px solid var(--color-neutral-20); color: var(--color-neutral-100); text-decoration: none; font-weight: 300; transition: all 0.3s; }
          .btn-secondary:hover { border-color: transparent; background: linear-gradient(to right, var(--color-primary-gradient-start), var(--color-primary-gradient-end)); color: #fff; }
          
          @media (max-width: 767px) {
            .contact-success-wrapper { padding: 40px 16px; min-height: 300px; gap: 16px; }
            .contact-success-wrapper h3 { font-size: 24px; }
            .contact-success-wrapper p { font-size: 14px; }
            .success-actions { flex-direction: column; width: 100%; gap: 12px; }
            .btn-primary, .btn-secondary { width: 100%; text-align: center; box-sizing: border-box; }
          }
        `}</style>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className={`contact-main-section reveal-group ${visible ? "is-visible" : ""}`}
    >
      <div className="contact-main-inner">
        <div className="contact-form-wrapper reveal-item">
          <p className="contact-form-notice">{formNotice}</p>
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div style={{ display: "none" }} aria-hidden="true">
              <input
                type="text"
                name="honeypot"
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <div className="form-rows-container">
              {formFields
                .reduce((acc: any[][], field: any) => {
                  if (field.halfWidth) {
                    const lastRow = acc[acc.length - 1];
                    if (lastRow && lastRow.length < 3) {
                      lastRow.push(field);
                    } else {
                      acc.push([field]);
                    }
                  } else {
                    acc.push([field]);
                  }
                  return acc;
                }, [])
                .map((row: any, rowIndex: number) => (
                  <div className="form-row" key={rowIndex}>
                    {row.map((field: any) => (
                      <div
                        className={`form-group ${!field.halfWidth ? "form-group-full" : ""}`}
                        key={field.name}
                      >
                        {field.type === "textarea" ? (
                          <div className="textarea-inner-wrapper">
                            <span className="textarea-inside-label">
                              {messageDes}
                              {field.required && (
                                <span className="required-star">*</span>
                              )}
                            </span>
                            <textarea
                              name={field.name}
                              placeholder={field.placeholder}
                              rows={5}
                              value={formState[field.name] || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={errors[field.name] ? "has-error" : ""}
                            />
                          </div>
                        ) : field.type === "select" ? (
                          <CustomSelect
                            name={field.name}
                            value={formState[field.name] || ""}
                            options={field.options || []}
                            placeholder={field.placeholder}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            hasError={!!errors[field.name]}
                          />
                        ) : (
                          <div
                            className={`input-wrapper ${errors[field.name] ? "has-error" : ""}`}
                          >
                            {field.required && (
                              <span className="required-star">*</span>
                            )}
                            <input
                              type={field.type}
                              name={field.name}
                              placeholder={field.placeholder}
                              value={formState[field.name] || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                        )}
                        {errors[field.name] && (
                          <p className="error-message">{errors[field.name]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
            <div className="form-checkbox">
              <input
                type="checkbox"
                id="gdpr"
                name="gdpr"
                checked={formState.gdpr || false}
                onChange={handleChange}
              />
              <label htmlFor="gdpr">{gdprText} </label>
            </div>
            {globalError && (
              <p className="error-message global-error">{globalError}</p>
            )}
            <button
              type="submit"
              className={`form-submit-btn ${isSubmitting ? "is-loading" : ""}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  {sumitLoading}
                </>
              ) : (
                <>
                  {submitText}
                  <Image src={submitIcon} alt="icon" width={12} height={12} />
                </>
              )}
            </button>
          </form>
        </div>

        <div className="contact-divider" />

        <div
          className="contact-info-wrapper reveal-item"
          style={revealDelay(200)}
        >
          <div className="contact-info-header">
            <h2 className="contact-info-title">{infoTitle}</h2>
            <p className="contact-info-branch">{infoBranch}</p>
            <div className="contact-socials">
              {socials.map((social: any, idx: number) => (
                <a href="#" key={idx} className="social-icon">
                  <Image
                    src={social.icon}
                    alt={social.name}
                    width={20}
                    height={20}
                  />
                </a>
              ))}
            </div>
          </div>
          <div className="contact-cards">
            {contactCards.map((card: any, idx: number) => {
              const isFullWidth = idx >= 2;
              return (
                <div
                  className={`contact-card ${isFullWidth ? "contact-card--full" : ""}`}
                  key={idx}
                >
                  <div className="contact-card-icon">
                    <Image
                      src={card.icon}
                      alt={card.label}
                      width={40}
                      height={40}
                    />
                  </div>
                  <div className="contact-card-content">
                    <p className="contact-card-label">{card.label}</p>
                    <p className="contact-card-value">{card.value}</p>
                  </div>
                  {card.action && (
                    <button
                      className="contact-card-action"
                      onClick={() => handleActionClick(idx, card.value)}
                    >
                      {copiedIndex === idx ? copySuccess : `[${card.action}]`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .contact-main-section {
          width: 100%;
          max-width: 1920px;
          margin: 0 auto;
          padding: 80px 0;
          display: flex;
          justify-content: center;
          background: var(--color-neutral-10, #F5F5F7);
        }
        .contact-main-inner {
          width: 100%;
          max-width: 1440px;
          display: flex;
          flex-direction: column;
          gap: 80px;
        }
        .contact-form-wrapper {
          flex: 1.5;
          width: 1200px; 
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .contact-form-notice {
          position: relative;
          padding-left: 18px; 
          font-size: 18px;
          font-weight: 400;
          line-height: 24px;
          color: var(--color-neutral-100, #262626);
          margin: 0;
          word-break: break-word;
        }
        
        .contact-form-notice::before {
          content: "";
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%); 
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: var(--color-neutral-100, #262626); 
        }
        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .form-rows-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .form-row {
          display: flex;
          flex-direction: row;
          gap: 16px;
        }
        .form-group {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .form-group-full {
          flex: 100%;
        }
        .input-wrapper {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 16px 20px;
          border-radius: 10px;
          gap:5px;
          background: var(--color-neutral-00, #FFF);
          box-sizing: border-box;
          transition: box-shadow 0.3s ease;
        }
        .input-wrapper.has-error {
          box-shadow: 0 0 0 1px #A32D2D inset;
        }
        .input-wrapper:focus-within {
          box-shadow: 0 0 0 1px var(--color-primary-gradient-end, #0a5694) inset;
        }
        .required-star {
          color: #F00;
          font-size: 14px;
          font-weight: 300;
          line-height: 20px;
          flex-shrink: 0;
        }
        .input-wrapper input {
          flex: 1;
          min-width: 0;
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          font-size: 14px;
          font-weight: 300;
          line-height: 20px;
          color: var(--color-neutral-100, #262626);
          font-family: inherit;
          box-sizing: border-box;
        }
        .textarea-inner-wrapper {
          position: relative;
          width: 100%;
        }
        .textarea-inside-label {
          position: absolute;
          top: 20px; 
          left: 20px; 
          font-size: 14px;
          color: var(--color-neutral-100, #262626);
          font-weight: 300;
          line-height:20px;
          pointer-events: none;
          z-index: 2;
          display: flex;
          align-items: center;
        }
        .form-group textarea {
          width: 100%;
          padding: 20px 20px 20px 120px;
          border: 0px solid var(--color-neutral-20, #e5e5e5);
          border-radius: 8px;
          font-size: 14px;
          color: var(--color-neutral-100, #262626);
          outline: none;
          transition: box-shadow 0.3s ease;
          font-family: inherit;
          background-color: #fff;
          box-sizing: border-box;
          resize: vertical;
          font-weight: 300;
        }
        .form-group textarea.has-error {
          box-shadow: 0 0 0 1px #A32D2D inset;
        }
        .form-group textarea:focus {
          box-shadow: 0 0 0 1px var(--color-primary-gradient-end, #0a5694) inset;
        }
        .error-message {
          font-size: 12px;
          color: #A32D2D;
          margin: 0;
          line-height: 1.2;
        }
        .global-error {
          margin-top: -10px;
          text-align: left;
        }
        .form-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }
        .form-checkbox input {
          margin-top: 3px;
          accent-color: var(--color-primary-gradient-end, #0a5694);
          flex-shrink: 0;
        }
        .form-checkbox label {
          font-size: 12px;
          color: var(--color-neutral-80, #666666);
          line-height: 1.5;
          word-break: break-word;
        }
        .privacy-link {
          color: var(--color-primary-gradient-end, #0a5694);
          text-decoration: underline;
        }
        .form-submit-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: fit-content;
          padding: 12px 30px;
          border-radius: 30px;
          background: var(--color-neutral-100, #262626);
          color: var(--color-neutral-00, #ffffff);
          font-size: 14px;
          font-weight: 300;
          border: none;
          cursor: pointer;
          transition: background 0.3s ease;
          white-space: nowrap;
          word-break: break-word;
        }
        .form-submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .form-submit-btn:not(:disabled):hover {
          background: linear-gradient(to right, var(--color-primary-gradient-start), var(--color-primary-gradient-end));
        }
        .spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        .contact-divider {
          height: 1px;
          background-color: var(--color-neutral-60, #c0c0c0);
        }
        .contact-info-wrapper {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: row;
          gap: 40px;
          align-items: flex-start;
        }
        .contact-info-header {
          display: flex;
          flex-direction: column;
          gap: 24px;
          flex: 0 0 560px;
        }
        .contact-info-title {
          font-size: 48px;
          font-weight: 700;
          line-height: 54px;
          color: var(--color-neutral-100, #262626);
          margin: 0 0 16px;
          word-break: break-word;
        }
        .contact-info-branch {
          font-size: 24px;
          font-weight: 400;
          line-height: 36px;
          color: var(--color-neutral-100, #262626);
          margin: 0;
          word-break: break-word;
        }
        .contact-socials {
          display: flex;
          gap: 12px; 
          flex-wrap: wrap;
        }
        .social-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          border-radius: 60px;
          background: var(--color-secondary-base, #7c7c7c12);
          transition: background-color 0.3s ease;
        }
        .contact-cards {
          flex: 1;
          min-width: 0;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          width: 100%;
        }
        .contact-card--full {
          grid-column: span 2;
        }
        .contact-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding:30px;
          background: #fff;
          border-radius: 16px;
          box-sizing: border-box;
          transition: box-shadow 0.3s ease;
        }
        .contact-card:hover {
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }
        .contact-card-icon {
          width: 40px;
          height: 40px;
          border-radius: 80px;
          flex-shrink: 0;
          background:var(--color-primary-base, #0070D5); 
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .contact-card-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .contact-card-label { 
          margin: 0;
          word-break: break-word;
          color: var(--color-neutral-100, #262626); 
          font-size: 16px; 
          font-weight: 600;
          line-height: 20px;  
        }
        .contact-card-value {
          color: var(--color-neutral-80, #666); 
          font-size: 14px;
          font-style: normal;
          font-weight: 300;
          line-height: 20px;  
          margin: 0;
          word-break: break-word;
          overflow-wrap: anywhere;
        }
        .contact-card-action {
          font-size: 14px;
          line-height: 20px;
          background: linear-gradient(to right, var(--color-primary-gradient-start), var(--color-primary-gradient-end));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent; 
          border: none;
          cursor: pointer;
          padding: 0;
          white-space: nowrap;
          font-weight: 300;
          flex-shrink: 0;
          align-self: center;
        }
        .contact-card-action:hover {
          text-decoration: underline;
        }
        @media (max-width: 1024px) {
          .contact-main-inner {
            flex-direction: column;
            gap: 40px;
          }
          .contact-divider {
            width: 100%;
            height: 1px;
          }
          .contact-form-wrapper,
          .contact-info-wrapper {
            flex: 1;
          }
          .contact-info-wrapper {
            flex-direction: column;
            gap: 30px;
          }
          .contact-info-header {
            flex: 1 1 auto;
            width: 100%;
          }
        }
        @media (max-width: 767px) {
          .contact-main-section {
            padding: 40px 16px;
          }
          .contact-main-inner {
            gap: 40px;
          }
          .contact-form-wrapper {
            width: 100%;
          }
          .contact-form-notice {
            font-size: 16px;
            padding-left: 14px;
          }
          .form-row {
            flex-direction: column;
            gap: 12px;
          }
          .textarea-inner-wrapper {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          .textarea-inside-label {
            position: static;
            margin-bottom: 4px;
            font-size: 14px;
          }
          .form-group textarea {
            padding: 16px 20px;
          }
          .input-wrapper {
            padding: 14px 16px;
          }
          .form-submit-btn {
            width: 100%;
            padding: 14px 20px;
          }
          .contact-info-header {
            gap: 20px;
          }
          
          .contact-info-title {
            font-size: 24px;
            margin: 0;
            line-height: 1.2;
          }
          .contact-info-branch {
            font-size: 16px;
            line-height: 1.5;
          }
          .social-icon {
            width: 32px;
            height: 32px;
          }
          .contact-cards {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          
          .contact-card--full {
            grid-column: span 1;
          }
          .contact-card {
            flex-direction: column;
            align-items: flex-start;
            padding: 16px;
            gap: 12px;
          }
          .contact-card-icon {
            width: 32px;
            height: 32px;
          }
          .contact-card-label {
            font-size: 15px;
          }
          .contact-card-value {
            font-size: 13px;
          }
          .contact-card-action {
            font-size: 12px;
            margin-top: 0;
            align-self: flex-start;
          }
        }
      `}</style>
    </section>
  );
};

/* ================= 3. Contact HQ Section ================= */
const ContactHeadquartersSection = ({
  contactJsonData,
  lang,
}: {
  contactJsonData: any;
  lang: string;
}) => {
  const { ref: sectionRef, visible } = useReveal<HTMLElement>();

  const hqTitle =
    contactJsonData?.["item_1791955573209_2wwsgvacf"]?.value ||
    "DEM Headquarters";
  const hqDesc =
    contactJsonData?.["item_1791955579945_bwcpi9ypr"]?.value ||
    "Please fill out the form below, and our senior engineers will provide you with an accurate quote and a free optimized design proposal within two hours.";
  const hqMap =
    "https://oss.yy-aicloud.com/uploads/20260924/6ab4b6966dbd94e681ae9d4a5e5698f08e852a0255702.svg";

  return (
    <section
      ref={sectionRef}
      className={`contact-hq-section reveal-group ${visible ? "is-visible" : ""}`}
    >
      <div className="contact-hq-inner">
        <div className="section-header-center">
          <h2 className="section-title reveal-item">{hqTitle}</h2>
          <p className="section-desc reveal-item" style={revealDelay(100)}>
            {hqDesc}
          </p>
        </div>
        <div className="contact-hq-map reveal-item" style={revealDelay(200)}>
          {/* 底图：SVG 世界地图（绝对定位，与叠加层完全重合） */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={hqMap}
            alt="DEM Headquarters Map"
            className="map-image"
            loading="lazy"
          />
          <MapSignalOverlay contactJsonData={contactJsonData} />
        </div>
      </div>

      <style>{`
        .contact-hq-section {
          width: 100%;
          max-width: 1920px;
          margin: 0 auto;
          padding: 100px 0;
          display: flex;
          justify-content: center;
        }
        .contact-hq-inner {
          width: 100%;
          max-width: 1440px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 48px;
        }
        .section-header-center {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: center;
          gap: 20px;
        }
        .section-title {
          color: var(--color-neutral-100, #262626);
          font-size: 48px;
          font-weight: 700;
          line-height: 54px;
          margin: 0;
          word-break: break-word;
        }
        .section-desc {
          font-size: 16px;
          color: var(--color-neutral-80, #666666);
          line-height: 24px;
          font-weight: 300;
          margin: 0;
          word-break: break-word;
        }
        .contact-hq-map {
          position: relative;
          width: 100%;
          aspect-ratio: 1440 / 632;
          border-radius: 20px;
          overflow: visible;
          background: #ffffff;
          isolation: isolate;
        }
        .map-image {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: fill;
          display: block;
          pointer-events: none;
          user-select: none;
        }
        .map-signal-overlay {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 2;
          display: block;
        }
        .map-signal-overlay .map-label {
          font-family: inherit;
          font-size: 11px;
          font-weight: 400;
          fill: #4B5563;
          user-select: none;
        }
        @media (max-width: 767px) {
          .contact-hq-section {
            padding: 40px 20px;
          }
          .section-title {
            font-size: 28px;
            line-height: 1.2;
          }
          .section-desc {
            font-size: 12px;
            line-height: 1.6;text-align: left;
          }
        }
      `}</style>
    </section>
  );
};

type LocationItem = {
  name: string;
  x: number;
  y: number;
  anchor?: "start" | "end";
};

type RouteData = {
  loc: LocationItem;
  path: Path2D;
  dist: number;
  delay: number;
  arrival: number;
  index: number;
};

// ========== 配置常量（与原SVG完全一致） ==========
const VIEW_W = 1440;
const VIEW_H = 632;
const CX = 480;
const CY = 300;

const START_DELAY = 0.2;
const STAGGER = 0.05;
const GROW_DUR = 0.4;
const FLOW_DUR = 1.4;
const HOLD_DUR = 2;
const FADE_DUR = 0;
const DEBUG = false;

// ========== 缓动函数（还原原spline曲线质感） ==========
const easeOutCubic = (t: number) =>
  1 - Math.pow(1 - Math.min(Math.max(t, 0), 1), 3);
const easeInCubic = (t: number) => Math.pow(Math.min(Math.max(t, 0), 1), 3);
type MapSignalOverlayProps = {
  contactJsonData: any;
};
const MapSignalOverlay: React.FC<MapSignalOverlayProps> = ({
  contactJsonData,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const routesRef = useRef<RouteData[]>([]);
  const elapsedRef = useRef(0); // 暂停时保存动画进度
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // 高清屏适配：按设备像素比缩放画布，避免模糊
    const dpr = window.devicePixelRatio || 1;
    canvas.width = VIEW_W * dpr;
    canvas.height = VIEW_H * dpr;
    canvas.style.width = "100%";
    canvas.style.height = "100%"; // 与原SVG preserveAspectRatio="none" 行为一致
    ctx.scale(dpr, dpr);
    const localCountry =
      contactJsonData?.["item_1792034436425_yi4y3czqu"]?.children || [];
    const LOCATIONS: LocationItem[] = localCountry.map((item: any) => {
      return {
        name: item.name,
        x: Number(item.x),
        y: Number(item.y),
        anchor: "start",
      };
    });
    // 预计算所有路线：贝塞尔路径、距离、时间节点
    const routes: RouteData[] = LOCATIONS.map((loc, i) => {
      const midX = (CX + loc.x) / 2;
      const midY = (CY + loc.y) / 2;
      const dist = Math.hypot(loc.x - CX, loc.y - CY);
      const ctrlY = midY - dist * 0.18;

      // 一次性生成贝塞尔路径，动画帧直接复用
      const path = new Path2D();
      path.moveTo(CX, CY);
      path.quadraticCurveTo(midX, ctrlY, loc.x, loc.y);

      const delay = START_DELAY + i * STAGGER;
      const arrival = delay + GROW_DUR;

      return { loc, path, dist, delay, arrival, index: i };
    });
    routesRef.current = routes;

    // 计算全局周期与时间节点
    const lastArrival = Math.max(...routes.map((r) => r.arrival));
    const CYCLE = lastArrival + HOLD_DUR + FADE_DUR;
    const OUT_START = FADE_DUR > 0 ? (CYCLE - FADE_DUR) / CYCLE : 0.9999;
    const fadeStartTime = OUT_START * CYCLE;
    const DASH_UNIT = 8; // 4px实线 + 4px空隙

    // ========== 绘制：总部中心点 ==========
    const drawHeadquarters = () => {
      ctx.save();
      ctx.translate(CX, CY);

      // 外层径向渐变（两层叠加还原原SVG透明度叠加效果）
      const outerGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 33.5);
      outerGrad.addColorStop(0, "rgba(0, 112, 213, 0.496)"); // 0.73 * 0.68
      outerGrad.addColorStop(1, "rgba(0, 112, 213, 0.061)"); // 0.09 * 0.68
      ctx.fillStyle = outerGrad;

      ctx.beginPath();
      ctx.arc(0, 0, 33.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(0, 0, 33.5, 0, Math.PI * 2);
      ctx.fill();

      // 中心线性渐变小圆
      const centerGrad = ctx.createLinearGradient(-7, 0, 7, 0);
      centerGrad.addColorStop(0, "#0a5694");
      centerGrad.addColorStop(1, "#0a5694");
      ctx.fillStyle = centerGrad;
      ctx.beginPath();
      ctx.arc(0, 0, 7, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    };

    // ========== 绘制：调试层 ==========
    const drawDebug = () => {
      ctx.strokeStyle = "red";
      ctx.lineWidth = 1;
      ctx.fillStyle = "red";
      ctx.font = "bold 11px sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";

      // 中心十字与圆
      ctx.beginPath();
      ctx.moveTo(CX - 20, CY);
      ctx.lineTo(CX + 20, CY);
      ctx.moveTo(CX, CY - 20);
      ctx.lineTo(CX, CY + 20);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(CX, CY, 12, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillText(`HQ (${CX},${CY})`, CX + 16, CY - 14);

      // 各地点坐标标记
      ctx.font = "9px sans-serif";
      routes.forEach(({ loc }) => {
        ctx.beginPath();
        ctx.moveTo(loc.x - 10, loc.y);
        ctx.lineTo(loc.x + 10, loc.y);
        ctx.moveTo(loc.x, loc.y - 10);
        ctx.lineTo(loc.x, loc.y + 10);
        ctx.stroke();
        ctx.fillText(`(${loc.x},${loc.y})`, loc.x + 8, loc.y - 8);
      });
    };

    // ========== 动画主循环 ==========
    const animate = (now: number) => {
      if (startTimeRef.current === null) startTimeRef.current = now;
      const elapsed = (now - startTimeRef.current) / 1000;
      const t = elapsed % CYCLE;

      // 清空画布
      ctx.clearRect(0, 0, VIEW_W, VIEW_H);

      // 1. 绘制总部
      drawHeadquarters();

      // 全局虚线偏移（所有路线同步，单次计算）
      const dashOffset = -((t / FLOW_DUR) * DASH_UNIT) % DASH_UNIT;

      // 2. 绘制所有路线
      routes.forEach((route) => {
        // 计算当前裁剪圆半径
        let radius: number;
        if (t < route.delay) {
          radius = 0;
        } else if (t < route.arrival) {
          const progress = (t - route.delay) / GROW_DUR;
          radius = (route.dist + 20) * easeOutCubic(progress);
        } else if (t < fadeStartTime) {
          radius = route.dist + 20;
        } else {
          radius = 0;
        }

        if (radius <= 0.5) return; // 半径过小跳过绘制

        ctx.save();
        // 圆形裁剪：实现辐射式出现效果
        ctx.beginPath();
        ctx.arc(CX, CY, radius, 0, Math.PI * 2);
        ctx.clip();

        // 绘制虚线路径
        ctx.strokeStyle = "rgba(77, 154, 255, 0.9)";
        ctx.lineWidth = 1.6;
        ctx.lineCap = "round";
        ctx.setLineDash([4, 4]);
        ctx.lineDashOffset = dashOffset;
        ctx.stroke(route.path);

        ctx.restore();
      });

      // 3. 绘制所有标记点与文字
      routes.forEach((route) => {
        // 计算标记点透明度
        let opacity: number;
        if (t < route.arrival) {
          opacity = 0;
        } else if (t < route.arrival + 0.4) {
          const progress = (t - route.arrival) / 0.4;
          opacity = easeOutCubic(progress);
        } else if (t < fadeStartTime) {
          opacity = 1;
        } else {
          opacity = 0;
        }

        if (opacity <= 0.01) return; // 透明跳过绘制

        ctx.save();
        ctx.globalAlpha = opacity;

        // 标记圆点
        ctx.fillStyle = "#0070d5";
        ctx.beginPath();
        ctx.arc(route.loc.x, route.loc.y, 4, 0, Math.PI * 2);
        ctx.fill();

        // 地点文字
        ctx.fillStyle = "#333";
        ctx.font = "500 12px 'Host Grotesk', sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        ctx.fillText(route.loc.name, route.loc.x, route.loc.y + 22);

        ctx.restore();
      });

      // 调试层
      if (DEBUG) drawDebug();

      animationRef.current = requestAnimationFrame(animate);
    };

    // ========== 暂停/恢复控制 ==========
    const pauseAnimation = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
        // 保存当前进度，恢复时无缝衔接
        if (startTimeRef.current !== null) {
          elapsedRef.current =
            (performance.now() - startTimeRef.current) / 1000;
        }
      }
    };

    const resumeAnimation = () => {
      if (animationRef.current) return;
      startTimeRef.current = performance.now() - elapsedRef.current * 1000;
      animationRef.current = requestAnimationFrame(animate);
    };

    // 启动动画
    resumeAnimation();

    // 离屏暂停：元素移出视口200px时停止动画
    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        ([entry]) => {
          entry.isIntersecting ? resumeAnimation() : pauseAnimation();
        },
        { rootMargin: "200px 0px" },
      );
      io.observe(canvas);
    }

    // 系统减少动画偏好适配
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleMotionChange = (e: MediaQueryListEvent) => {
      e.matches ? pauseAnimation() : resumeAnimation();
    };
    mq.addEventListener("change", handleMotionChange);
    if (mq.matches) pauseAnimation();

    // 清理
    return () => {
      pauseAnimation();
      io?.disconnect();
      mq.removeEventListener("change", handleMotionChange);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="map-signal-overlay"
      aria-hidden="true"
      style={{ display: "block", overflow: "hidden" }}
    />
  );
};

/* ================= 页面主入口 ================= */
export default function ContactPageContent({ lang }: { lang: string }) {
  const { settingData } = useSiteData();
  const contactJsonData = settingData?.contactUsJson || {};

  return (
    <main className="relative">
      <style>{`
        .section-header-center {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: center;
          gap: 16px;
        }
        a {
          text-decoration: none;
        }
        @media (min-width: 768px) {
          :where(.reveal-group) :where(.reveal-item) {
            opacity: 0;
            transform: translate3d(0, 40px, 0);
            transition:
              opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1),
              transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
            transition-delay: var(--reveal-delay, 0ms);
            will-change: opacity, transform;
            backface-visibility: hidden;
          }
          :where(.reveal-group.is-visible) :where(.reveal-item) {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          :where(.reveal-item) {
            opacity: 1 !important;
            transform: none !important;
            transition: none !important;
            animation: none !important;
          }
        }
      `}</style>

      <Navbar isVisible={true} isSolid={true} />
      <ContactBannerSection contactJsonData={contactJsonData} lang={lang} />
      <ContactMainSection contactJsonData={contactJsonData} lang={lang} />
      <ContactHeadquartersSection
        contactJsonData={contactJsonData}
        lang={lang}
      />
      <Footer />
    </main>
  );
}
